<script lang="ts">
	import Router from 'svelte-spa-router';
	import { format } from '../services/i18n';
	import { location } from 'svelte-spa-router';
	import { LoginLayout } from '@Shelly/shelly-design-system';
	import { user_settings } from 'src/stores/account/user';
import { initializing_language } from 'src/stores/session/preferences';
	import { LANG_DICT, TIMEZONE_LANG_MAP } from 'src/constants';
	import { changeLanguage } from 'src/components/Profiles/profiles_helpers';
	import { onMount } from 'svelte';
	import type { LangaugeDropdownItem } from 'src/types/interfaces';

	import Login from './Login.svelte';
	import Signup from './Signup.svelte';
	import EmailVerification from './EmailVerification.svelte';
	import ChangeEmail from './ChangeEmail.svelte';
	import RequestPasswordReset from './RequestPasswordReset.svelte';
	import PasswordReset from './PasswordReset.svelte';
	import RequestDeleteAccount from './RequestDeleteAccount.svelte';
	import AccountDelete from './AccountDelete.svelte';
	import FastLogin from './FastLogin.svelte';
	import InstallerLogin from './InstallerLogin.svelte';
	import SubAccountSignUp from './SubAccountSignUp.svelte';
	import LangDropdown from 'src/components/HelperComponents/LangDropdown.svelte';

	const routes = {
		'/login': Login,
		'/signup': Signup,
		'/email-verification': EmailVerification,
		'/change-email': ChangeEmail,
		'/request-password-reset': RequestPasswordReset,
		'/password-reset': PasswordReset,
		'/request-account-delete': RequestDeleteAccount,
		'/account-delete': AccountDelete,
		'/fast-login': FastLogin,
		'/installer-login': InstallerLogin,
		'/register_subaccount/:token/:email': SubAccountSignUp,
	};
	let initialLanguage: string;
	let dropdownItemsData: LangaugeDropdownItem[];
	let showDropdownMenu = false;
	let currentLangIso: string;

	const onLanguageChange = (dropdownItem: Partial<LangaugeDropdownItem>) => {
		currentLangIso = formatLanguageIso(dropdownItem.language);
		showDropdownMenu = false;

		changeLanguage(dropdownItem);
	};

	$: if ($location) {
		showDropdownMenu = false;
	}

	const setLanguageDropdownItems = () => {
		const languages = Object.entries(LANG_DICT);
		const dropdownItems = [];

		for (const [iso, name] of languages) {
			const dropdownItem = {
				title: name,
				subtitle: formatLanguageIso(iso),
				language: iso,
			};

			dropdownItems.push(dropdownItem);
		}

		return dropdownItems;
	};

	const formatLanguageIso = (iso: string = 'en'): string => {
		if (iso?.length > 2) {
			return iso.slice(0, 2).toUpperCase();
		}

		return iso?.toUpperCase();
	};

	onMount(() => {
		dropdownItemsData = setLanguageDropdownItems();

		/**
		 * Apply the timezone-based default ONLY when the user has no existing
		 * language preference (e.g. very first launch). Otherwise we would
		 * overwrite a language the user explicitly picked on the login/register
		 * screen every time this layout remounts (incl. after a refresh).
		 */
		const existingLang = $user_settings?.lang || $initializing_language;

		if (existingLang) {
			currentLangIso = formatLanguageIso(existingLang);
			return;
		}

		initialLanguage = TIMEZONE_LANG_MAP[new Intl.DateTimeFormat().resolvedOptions().timeZone];

		if (initialLanguage) {
			currentLangIso = initialLanguage.toUpperCase();
			changeLanguage({ language: initialLanguage });
		} else {
			currentLangIso = formatLanguageIso();
		}
	});
</script>

<LoginLayout
	backgroundImgSrc="./images/loginBackground.jpeg"
	subtitle={$format('login_welcome_subtitle', {
		default: 'Welcome to the Shelly Experience',
	})}
	logoUrl="./images/shelly-logo.svg"
>
	<svelte:fragment slot="title">
		{@html $format('login_tagline_title', {
			default: 'Explore the Possibilities',
		})}
	</svelte:fragment>
	<div slot="form">
		{#if $location !== '/fast-login'}
			<div class="lng-bar text-right">
				<LangDropdown
					bind:showDropdown={showDropdownMenu}
					dropdownItems={dropdownItemsData}
					lang={currentLangIso}
					selectedLang={$user_settings.lang}
					on:change-lang={(e) => {
						onLanguageChange(e.detail);
					}}
				/>
			</div>
		{/if}
		<Router {routes} />
	</div>
</LoginLayout>

<style>
	.lng-bar {
		float: right;
		margin: 0 0 var(--spacing-s) var(--spacing-s);
	}
</style>
